'use client';

import { createContext, createElement, useContext, type ReactNode } from 'react';
import { useScanStatusController } from './use-scan-status-controller';
import type {
    ScanStatusController,
    ScanJobData,
    ScanStatusState,
} from './use-scan-status.types';

const ScanStatusContext = createContext<ScanStatusController | null>(null);

export function ScanStatusProvider({ children }: { children: ReactNode }) {
  const value = useScanStatusController();
  return createElement(ScanStatusContext.Provider, { value }, children);
}

export function useScanStatus(): ScanStatusController {
  const context = useContext(ScanStatusContext);
  if (!context) {
    throw new Error('useScanStatus must be used within ScanStatusProvider');
  }

  return context;
}

/**
 * Non-throwing variant - returns null when no ScanStatusProvider is mounted.
 * Useful for components that can degrade gracefully when scan status is
 * unavailable (e.g., during isolated unit tests or pages outside the
 * dashboard shell).
 */
export function useScanStatusOptional(): ScanStatusController | null {
  return useContext(ScanStatusContext);
}

export type {
  ScanStatusController,
  ScanJobData,
  ScanStatusState,
};
